import { useEffect } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { DjPreviewCard } from "@/components/setup/dj-preview-card";
import { MenuPreviewCard } from "@/components/setup/menu-preview-card";
import { NamesPreviewCard } from "@/components/setup/names-preview-card";
import { SharePreviewCard } from "@/components/setup/share-preview-card";
import type { SetupStepId } from "@/components/setup/steps";
import { ThemeCardPreview } from "@/components/setup/theme-card-preview";
import { TimelinePreviewCard } from "@/components/setup/timeline-preview-card";
import { CUSTOM_TEMPLATE_ID, resolveCurrentLook } from "@/lib/templates";
import type { GetMySetupResp } from "@/lib/types";
import { cn } from "@/lib/utils";

function PreviewButton({
  onClick,
  ariaLabel,
  className,
  children,
}: {
  onClick: () => void;
  ariaLabel: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        "overflow-hidden rounded-lg border text-left transition-shadow hover:border-primary/40 hover:shadow-md",
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * Every topic of the buyer's page as a visual preview card — the
 * dashboard's settings overview. Tap any card to open the matching
 * setup step inline (`onEdit`); no separate "Edit" buttons.
 */
export function PageSettingsCard({
  setup,
  onEdit,
}: {
  setup: GetMySetupResp;
  onEdit: (step: SetupStepId) => void;
}) {
  const { t } = useTranslation();
  const { site, config, djPinSet, templates } = setup;
  const { palette, fontScheme } = resolveCurrentLook(config);

  // The theme card's inline font-family only actually renders once its
  // Google Fonts stylesheet is loaded — same singleton-link pattern as
  // StepLook's AI preview (step-look.tsx), adapted for one steady card
  // instead of one active preview among several.
  useEffect(() => {
    const cssUrl = fontScheme.cssUrl;
    const id = "page-settings-look-font";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== cssUrl) link.href = cssUrl;
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [fontScheme.cssUrl]);

  if (!site) return null;

  const templateId = config?.templateId;
  const lookLabel =
    templateId && templateId !== CUSTOM_TEMPLATE_ID
      ? (templates.find((tpl) => tpl.id === templateId)?.name ??
        t("setup.settings.templateFallback"))
      : t("setup.settings.customLook");

  const menuOn = config?.permissions?.allowMenu === true;
  const timelineOn = config?.permissions?.allowTimeline === true;
  const djOn = config?.permissions?.allowDjRequests === true;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">{site.displayName}</h2>
        <p className="text-sm text-muted-foreground">
          {t("setup.settings.subtitle")}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <PreviewButton onClick={() => onEdit("basics")} ariaLabel={t("setup.settings.editBasics")}>
          <NamesPreviewCard site={site} />
        </PreviewButton>
        <PreviewButton onClick={() => onEdit("look")} ariaLabel={t("setup.settings.editLook")}>
          <ThemeCardPreview
            palette={palette}
            fontScheme={fontScheme}
            heading={site.displayName}
            label={lookLabel}
          />
        </PreviewButton>
        <PreviewButton onClick={() => onEdit("menu")} ariaLabel={t("setup.settings.editMenu")}>
          <MenuPreviewCard menu={config?.menu} on={menuOn} />
        </PreviewButton>
        <PreviewButton onClick={() => onEdit("timeline")} ariaLabel={t("setup.settings.editTimeline")}>
          <TimelinePreviewCard timeline={config?.timeline} on={timelineOn} />
        </PreviewButton>
        <PreviewButton onClick={() => onEdit("dj")} ariaLabel={t("setup.settings.editDj")}>
          <DjPreviewCard on={djOn} pinSet={djPinSet} />
        </PreviewButton>
        <PreviewButton onClick={() => onEdit("done")} ariaLabel={t("setup.settings.viewShare")}>
          <SharePreviewCard slug={site.slug} />
        </PreviewButton>
      </div>
    </div>
  );
}
